import { useState } from 'react';
import { LogIn, KeyRound, Shield, Eye, EyeOff, ArrowLeft, Sparkles, ChevronLeft } from 'lucide-react';
import { loginPatient, loginAdmin } from '../lib/mock';
import { LogoWordmark } from '../components/Logo';

export default function LoginScreen({ onPatientLogin, onAdminLogin }) {
  const [stage, setStage] = useState('welcome'); // 'welcome' | 'pin' | 'admin'
  const [pin, setPin] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePatient = async () => {
    setErr('');
    if (!pin.trim()) { setErr('أدخلي رقمك السري'); return; }
    setLoading(true);
    try {
      const found = await loginPatient(pin.trim());
      if (found) onPatientLogin(found);
      else setErr('رقم سري غير صحيح');
    } catch (e) { setErr('تعذّر الاتصال'); }
    finally { setLoading(false); }
  };

  const handleAdmin = async () => {
    setErr('');
    if (!pass.trim()) { setErr('أدخل كلمة المرور'); return; }
    setLoading(true);
    try {
      const ok = await loginAdmin(pass);
      if (ok) onAdminLogin();
      else setErr('كلمة مرور غير صحيحة');
    } catch (e) { setErr('تعذّر الاتصال'); }
    finally { setLoading(false); }
  };

  const onKey = (e) => {
    if (e.key !== 'Enter') return;
    if (stage === 'pin') handlePatient();
    else if (stage === 'admin') handleAdmin();
  };

  const goBack = () => { setStage('welcome'); setErr(''); setPin(''); setPass(''); };

  return (
    <div className="phone-shell-frame">
      <div className="phone-shell relative overflow-hidden">
        {/* Ambient orbs */}
        <div className="orb animate-float" style={{ top: '-10%', right: '-15%', width: '280px', height: '280px', background: '#f4a09c', opacity: 0.32 }}></div>
        <div className="orb animate-float" style={{ top: '35%', left: '-15%', width: '240px', height: '240px', background: '#b8afff', opacity: 0.35, animationDelay: '1s' }}></div>
        <div className="orb animate-float" style={{ bottom: '-10%', right: '-10%', width: '260px', height: '260px', background: '#a4d6cc', opacity: 0.32, animationDelay: '2s' }}></div>

        {/* Admin corner button (top-left in RTL viewport) */}
        {stage !== 'admin' && (
          <button
            onClick={() => { setStage('admin'); setErr(''); }}
            className="absolute top-4 left-4 z-30 flex items-center gap-1.5 bg-white/70 backdrop-blur border border-[var(--border-soft)] rounded-full px-3 py-1.5 text-[11px] font-bold hover:bg-white transition-colors"
            style={{ color: 'var(--text-2)' }}
          >
            <Shield className="w-3 h-3" style={{ color: 'var(--secondary-dark)' }} />
            مشرف
          </button>
        )}

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-10">
          {/* Logo & branding */}
          <div className="text-center mb-10 animate-slide-up">
            <LogoWordmark size={110} />
          </div>

          {/* Welcome CTA */}
          {stage === 'welcome' && (
            <div className="w-full max-w-sm animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <button
                onClick={() => setStage('pin')}
                className="group w-full relative overflow-hidden rounded-3xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #b8afff 0%, #8b7ff0 40%, #7dd3c3 100%)' }}
              >
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/25 blur-2xl"></div>
                <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-white/20 blur-2xl"></div>
                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/25 backdrop-blur px-3 py-1 mb-3">
                    <Sparkles className="w-3 h-3 text-white" fill="white" />
                    <span className="text-[11px] font-extrabold text-white/95 uppercase tracking-wider">ابدئي رحلتك</span>
                  </div>
                  <h2 className="text-white text-2xl font-extrabold mb-1.5 leading-tight drop-shadow-sm">
                    من هنا… يبدأ سلامك
                  </h2>
                  <p className="text-white/90 text-[12.5px] leading-relaxed mb-4 max-w-[280px] mx-auto">
                    ملاذٌ دافئ ينتظرك، فيه من يفهمك ويرافقك في لحظتك
                  </p>
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-white/95 backdrop-blur px-5 py-2.5 shadow-md group-hover:scale-105 transition-transform">
                    <span className="text-[13px] font-extrabold" style={{ color: '#6a5cd8' }}>أدخلي إلى ملاذك</span>
                    <ChevronLeft className="w-4 h-4" style={{ color: '#6a5cd8' }} strokeWidth={2.5} />
                  </div>
                </div>
              </button>

              <div className="text-center mt-6 text-[10.5px]" style={{ color: 'var(--text-3)' }}>
                جلسة مشفّرة • خصوصية تامة • دعم دافئ
              </div>
            </div>
          )}

          {/* Patient PIN entry */}
          {stage === 'pin' && (
            <div className="w-full max-w-sm animate-slide-up">
              <div className="card-soft p-5">
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={goBack} className="w-8 h-8 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--border-soft)] flex items-center justify-center transition-colors">
                    <ArrowLeft className="w-3.5 h-3.5 rotate-180" style={{ color: 'var(--text-2)' }} />
                  </button>
                  <div className="flex-1">
                    <div className="text-[13.5px] font-extrabold" style={{ color: 'var(--text)' }}>أهلاً بعودتك 🌿</div>
                    <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>أدخلي رقمك السري للمتابعة</div>
                  </div>
                </div>

                <label className="text-[11px] font-bold block mb-2" style={{ color: 'var(--text-2)' }}>الرقم السري</label>
                <div className="relative">
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary-bg)' }}>
                    <KeyRound className="w-4 h-4" style={{ color: 'var(--primary-dark)' }} />
                  </div>
                  <input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    value={pin}
                    onChange={e => setPin(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="● ● ● ●"
                    className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[var(--primary)] rounded-2xl py-4 pr-14 pl-4 text-[var(--text)] outline-none transition-all text-lg tracking-[0.4em] text-center font-bold"
                  />
                </div>

                <button
                  onClick={handlePatient}
                  disabled={loading}
                  className="w-full mt-4 py-4 rounded-2xl text-white font-extrabold text-[15px] flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-95 hover:shadow-lg disabled:opacity-50 shadow-md active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}
                >
                  <LogIn className="w-4 h-4" />
                  {loading ? 'جارٍ الدخول...' : 'دخول آمن'}
                </button>

                {err && (
                  <div className="mt-3 text-center bg-[var(--danger-bg)] border border-[var(--danger)]/30 rounded-xl px-3 py-2.5 text-[12.5px] font-semibold animate-fade-in" style={{ color: '#c26666' }}>
                    {err}
                  </div>
                )}
              </div>

              <div className="text-center mt-4 text-[11px]" style={{ color: 'var(--text-3)' }}>
                رقمك السري خاص بك — احتفظي به سراً
              </div>
            </div>
          )}

          {/* Admin entry */}
          {stage === 'admin' && (
            <div className="w-full max-w-sm animate-slide-up">
              <div className="card-soft p-5">
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={goBack} className="w-8 h-8 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--border-soft)] flex items-center justify-center transition-colors">
                    <ArrowLeft className="w-3.5 h-3.5 rotate-180" style={{ color: 'var(--text-2)' }} />
                  </button>
                  <div className="flex-1">
                    <div className="text-[13.5px] font-extrabold flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
                      <Shield className="w-3.5 h-3.5" style={{ color: 'var(--secondary-dark)' }} />
                      دخول المشرف
                    </div>
                    <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>خاص بإدارة ملفات المريضات</div>
                  </div>
                </div>

                <label className="text-[11px] font-bold block mb-2" style={{ color: 'var(--text-2)' }}>كلمة المرور</label>
                <div className="relative">
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--secondary-bg)' }}>
                    <Shield className="w-4 h-4" style={{ color: 'var(--secondary-dark)' }} />
                  </div>
                  <input
                    autoFocus
                    type={showPass ? 'text' : 'password'}
                    value={pass}
                    onChange={e => setPass(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="••••••••"
                    className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[var(--secondary)] rounded-2xl py-4 pr-14 pl-12 text-[var(--text)] outline-none transition-all text-[15px]"
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-[var(--text-2)]">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <button
                  onClick={handleAdmin}
                  disabled={loading}
                  className="w-full mt-4 py-4 rounded-2xl text-white font-extrabold text-[15px] flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-95 hover:shadow-lg disabled:opacity-50 shadow-md active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }}
                >
                  <LogIn className="w-4 h-4" />
                  {loading ? 'جارٍ الدخول...' : 'دخول آمن'}
                </button>

                {err && (
                  <div className="mt-3 text-center bg-[var(--danger-bg)] border border-[var(--danger)]/30 rounded-xl px-3 py-2.5 text-[12.5px] font-semibold animate-fade-in" style={{ color: '#c26666' }}>
                    {err}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
